<template>
  <div class="list-page-container">
    <!-- 顶部操作区：副标题与创建按钮 -->
    <div class="control-panel">
      <div class="control-container">
        <div class="header-info">
          <i class="el-icon-folder"></i>
          <span>{{ $t('userManagement.userGroupListTips') }}</span>
        </div>
        <div class="action-wrapper">
          <el-button type="primary" @click="handleCreateGroup" v-if="hasPermission('sys:usergroup:save')">
            <i class="el-icon-plus"></i> {{ $t('userManagement.createGroup') }}
          </el-button>
        </div>
      </div>
    </div>

    <!-- 统一控制中心：筛选模块 -->
    <div class="control-panel has-query">
      <div class="control-container">
        <div class="filter-wrapper">
          <div class="filter-content">
            <el-form :inline="true" :model="searchForm" class="filter-form">
              <el-form-item>
                <el-input
                  v-model="searchForm.name"
                  :placeholder="$t('userManagement.searchGroupName')"
                  prefix-icon="el-icon-search"
                  clearable
                  @keyup.enter.native="handleSearch"
                  @clear="handleSearch"
                  class="filter-input"
                ></el-input>
              </el-form-item>
              <el-form-item class="search-btns">
                <el-button type="primary" @click="handleSearch" icon="el-icon-search">{{ $t('common.search') }}</el-button>
                <el-button @click="handleReset" icon="el-icon-refresh">{{ $t('common.reset') }}</el-button>
              </el-form-item>
            </el-form>
          </div>
        </div>
      </div>
    </div>

    <!-- 用户分组列表 -->
    <el-card class="user-group-list-card">
      <el-table
        :data="paginatedGroups"
        v-loading="loading"
        height="100%"
        style="width: 100%"
        class="custom-table"
        @selection-change="handleSelectionChange"
        :element-loading-text="$t('common.loading')"
      >
        <el-table-column type="selection" width="55"></el-table-column>
        <el-table-column prop="name" :label="$t('userManagement.groupName')"></el-table-column>
        <el-table-column prop="description" :label="$t('userManagement.description')"></el-table-column>
        <el-table-column prop="user_ids" :label="$t('userManagement.memberCount')" width="120">
          <template slot-scope="scope">
            <el-tag size="small">{{ scope.row.user_ids ? scope.row.user_ids.length : 0 }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="createDate" :label="$t('common.createdAt')">
          <template slot-scope="scope">
            {{ formatDate(scope.row.createDate) }}
          </template>
        </el-table-column>
        <el-table-column v-if="hasAnyActionPermission" :label="$t('common.actions')" :width="actionColumnWidth" fixed="right" align="center">
          <template slot-scope="scope">
            <div class="action-buttons">
              <el-button
                type="primary"
                size="mini"
                plain
                @click="handleViewDetail(scope.row.id)"
                icon="el-icon-view"
                :aria-label="$t('common.detail')"
                v-if="hasPermission('sys:usergroup:info')"
              >
                {{ $t('common.detail') }}
              </el-button>
              <el-button
                type="warning"
                size="mini"
                plain
                @click="handleEditGroup(scope.row.id)"
                icon="el-icon-edit"
                :aria-label="$t('common.edit')"
                v-if="hasPermission('sys:usergroup:update')"
              >
                {{ $t('common.edit') }}
              </el-button>
              <el-button
                type="danger"
                size="mini"
                plain
                @click="handleDeleteGroup(scope.row.id)"
                icon="el-icon-delete"
                :aria-label="$t('common.delete')"
                v-if="hasPermission('sys:usergroup:delete')"
              >
                {{ $t('common.delete') }}
              </el-button>
            </div>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination-container">
        <el-pagination
          :current-page="page"
          :page-sizes="[10, 20, 50, 100]"
          :page-size="limit"
          layout="total, sizes, prev, pager, next, jumper"
          :total="filteredGroups.length"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        ></el-pagination>
      </div>
    </el-card>

    <!-- 底部悬浮批量操作栏 -->
    <transition name="el-zoom-in-bottom">
      <div v-if="selectedGroups.length > 0" class="batch-footer-bar">
        <div class="batch-info">
          <i class="el-icon-info"></i>
          <span>{{ $t('userManagement.selectedGroupCount', { count: selectedGroups.length }) }}</span>
        </div>
        <div class="batch-buttons">
          <el-button type="danger" size="small" icon="el-icon-delete" @click="handleBatchDelete" v-if="hasPermission('sys:usergroup:delete')">
            {{ $t('userManagement.deleteSelectedGroup') }}
          </el-button>
          <el-divider direction="vertical" v-if="hasPermission('sys:usergroup:delete')"></el-divider>
          <el-button type="text" @click="selectedGroups = []">{{ $t('common.cancel') }}</el-button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { hasPermission } from '@/utils/permission'

export default {
  name: 'UserGroupList',
  data() {
    return {
      searchForm: {
        name: ''
      },
      loading: false,
      page: 1,
      limit: 10,
      selectedGroups: []
    }
  },
  computed: {
    allGroups() {
      return this.$store.getters.allUserGroups
    },
    // 操作列权限判断
    canViewGroup() {
      return this.hasPermission('sys:usergroup:info')
    },
    canEditGroup() {
      return this.hasPermission('sys:usergroup:update')
    },
    canDeleteGroup() {
      return this.hasPermission('sys:usergroup:delete')
    },
    // 操作列可见按钮数量（基于权限）
    visibleActionCount() {
      let count = 0
      if (this.canViewGroup) count += 1
      if (this.canEditGroup) count += 1
      if (this.canDeleteGroup) count += 1
      return count
    },
    // 是否有任何操作权限
    hasAnyActionPermission() {
      return this.visibleActionCount > 0
    },
    // 操作列动态宽度
    actionColumnWidth() {
      const count = this.visibleActionCount
      const widthMap = { 1: 100, 2: 170, 3: 240 }
      return widthMap[count] || 0
    },
    filteredGroups() {
      let groups = [...this.allGroups]
      
      // 筛选逻辑
      if (this.searchForm.name) {
        groups = groups.filter(group => 
          group.name.toLowerCase().includes(this.searchForm.name.toLowerCase())
        )
      }
      
      return groups
    },
    paginatedGroups() {
      const start = (this.page - 1) * this.limit
      const end = start + this.limit
      return this.filteredGroups.slice(start, end)
    }
  },
  created() {
    this.fetchData()
  },
  methods: {
    hasPermission,
    async fetchData() {
      this.loading = true
      try {
        await this.$store.dispatch('fetchUserGroups')
      } catch (error) {
        this.$message.error(this.$t('userManagement.fetchGroupError'))
      } finally {
        this.loading = false
      }
    },
    // 格式化日期
    formatDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      const seconds = String(date.getSeconds()).padStart(2, '0')
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
    },
    
    // 创建分组
    handleCreateGroup() {
      this.$router.push('/user/group/create')
    },
    
    // 查看分组详情
    handleViewDetail(id) {
      this.$router.push(`/user/group/detail/${id}`)
    },
    
    // 编辑分组
    handleEditGroup(id) {
      this.$router.push(`/user/group/edit/${id}`)
    },
    
    // 删除分组
    handleDeleteGroup(id) {
      const group = this.$store.getters.userGroupById(id)
      this.$confirm(
        this.$t('userManagement.deleteGroupConfirm', { name: group.name }),
        this.$t('common.confirm'),
        {
          confirmButtonText: this.$t('common.confirm'),
          cancelButtonText: this.$t('common.cancel'),
          type: 'warning'
        }
      ).then(() => {
        this.$store.dispatch('deleteUserGroup', [id]).then(() => {
          this.$message.success(this.$t('userManagement.deleteGroupSuccess'))
          this.fetchData()
        }).catch(err => {
          this.$message.error(this.$t('userManagement.deleteGroupError'))
        })
      }).catch(() => {
        this.$message.info(this.$t('common.cancel'))
      })
    },
    
    // 搜索分组
    handleSearch() {
      this.page = 1
    },

    // 重置搜索
    handleReset() {
      this.searchForm = {
        name: ''
      }
      this.page = 1
    },

    // 分页处理
    handleSizeChange(size) {
      this.limit = size
      this.page = 1
    },

    handleCurrentChange(page) {
      this.page = page
    },
    
    // 批量选择
    handleSelectionChange(val) {
      this.selectedGroups = val
    },
    
    // 批量删除
    handleBatchDelete() {
      const groupIds = this.selectedGroups.map(group => group.id)
      
      this.$confirm(
        this.$t('userManagement.batchDeleteGroupConfirm', { count: groupIds.length }),
        this.$t('common.confirm'),
        {
          confirmButtonText: this.$t('common.confirm'),
          cancelButtonText: this.$t('common.cancel'),
          type: 'warning'
        }
      ).then(() => {
        // 批量删除分组
        this.$store.dispatch('deleteUserGroup', groupIds).then(() => {
          this.$message.success(this.$t('userManagement.batchDeleteGroupSuccess'))
          this.selectedGroups = []
          this.fetchData()
        }).catch(err => {
          this.$message.error(this.$t('userManagement.batchDeleteGroupError'))
        })
      }).catch(() => {
        this.$message.info(this.$t('common.cancel'))
      })
    }
  }
}
</script>

<style scoped lang="scss">
@import "@/styles/list-page.scss";

/* 用户分组列表页面特有样式 */
.list-page-container {
  .filter-input {
    width: 240px;
  }

  .filter-wrapper {
    .filter-content {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 20px;
    }
  }
}
</style>